// src/store/modules/auth/index.ts
import { ActionContext } from "vuex";
import router from "@/router";
import { getNewAccessToken } from "@/modules/auth/api";
import {
  getRefreshToken,
  setAccessToken,
  setRefreshToken,
  cleanTokensData,
  getJWTPayload,
} from "@/modules/common/utils/tokens";

// Типы состояния и payload
interface AuthState {
  TOKEN_USER_DATA: any | null;
}

interface CompleteYandexSigninPayload {
  accessToken: string;
  refreshToken: string;
}

export default {
  namespaced: true,
  state: (): AuthState => ({
    TOKEN_USER_DATA: null,
  }),

  getters: {
    IS_LOGIN: (state: AuthState) => state.TOKEN_USER_DATA !== null,
    GET_TOKEN_USER_DATA: (state: AuthState) => state.TOKEN_USER_DATA,
  },

  mutations: {
    SET_TOKEN_USER_DATA(state: AuthState, user: any | null) {
      state.TOKEN_USER_DATA = user;
    },
  },

  actions: {
    /**
     * Завершение авторизации через Яндекс. Сохраняет токены и отправляет пользователя на домашнюю страницу.
     */
    COMPLETE_YANDEX_SIGNIN(
      { commit }: ActionContext<AuthState, any>,
      { accessToken, refreshToken }: CompleteYandexSigninPayload,
    ) {
      setAccessToken(accessToken);
      setRefreshToken(refreshToken);

      const { user_data } = getJWTPayload(accessToken);
      commit("SET_TOKEN_USER_DATA", user_data);

      router.push({ name: "home" });
    },

    /**
     * Автоматический вход при загрузке приложения, используя refresh токен.
     */
    async AUTO_SIGN_IN({ dispatch, commit }: ActionContext<AuthState, any>) {
      try {
        const refreshToken = getRefreshToken();
        if (!refreshToken) {
          dispatch("SIGN_OUT");
          return;
        }
        const newAccessToken = await dispatch("FETCH_NEW_ACCESS_TOKEN", refreshToken);
        if (!newAccessToken) {
          dispatch("SIGN_OUT");
          return;
        }
        setAccessToken(newAccessToken);

        const { user_data } = getJWTPayload(newAccessToken);
        commit("SET_TOKEN_USER_DATA", user_data);

        // Обновляем данные и пермишены пользователя, если необходимо
        await dispatch("users/FETCH_USER_ME_WITH_PERMISSIONS", null, { root: true });
      } catch {
        dispatch("SIGN_OUT");
      }
    },

    /**
     * Выход из системы.
     */
    SIGN_OUT({ dispatch }: ActionContext<AuthState, any>) {
      dispatch("CLEAR_ALL_AUTH_DATA");
      router.push({ name: "signin" });
    },

    /**
     * Очистка всех данных аутентификации.
     */
    CLEAR_ALL_AUTH_DATA({ commit }: ActionContext<AuthState, any>) {
      cleanTokensData();
      commit("SET_TOKEN_USER_DATA", null);
    },

    /**
     * Получение нового access токена с использованием refresh токена.
     */
    async FETCH_NEW_ACCESS_TOKEN(_: ActionContext<AuthState, any>, refreshToken: string) {
      try {
        const res = await getNewAccessToken(refreshToken);
        if (res.status !== 200) {
          return null;
        }
        return res.data.access_token;
      } catch (error: any) {
        console.error("Error FETCH_NEW_ACCESS_TOKEN:", error);
        return null;
      }
    },
  },
};
